// @flow
/** @jsx jsx */

import { Checkbox, TextInput } from '@zapier/design-system';
import { css, jsx } from '@emotion/core';

import CodeEditor from 'app/developer-v3/components/CodeEditor';
import NonFormaticFieldWrapper from 'app/developer-v3/components/NonFormaticFieldWrapper';
import RequestEditor from 'app/developer-v3/components/RequestEditor';
import SuggestionsProvider from 'app/common/components/AutoCompleteTextInput/SuggestionsContext';
import { cleanRequest } from 'app/developer-v3/utils';

import type { ErrorList } from 'app/developer-v3/types/validator';
import type { ViolationTypes } from 'app/developer-v3/types';

const Styles = {
  checkbox: css`
    display: flex;
    align-items: center;
  `,
  checkboxLabel: css`
    margin-left: 12px;
  `,
};

type Field = {
  boolHelpText?: string,
  extraProps?: {},
  helpText?: string,
  key: string,
  label: string,
  readOnly?: boolean,
  required?: boolean,
  type: 'text-input' | 'boolean-input' | 'request-editor',
};

type Props = {
  fields: Array<Field>,
  meta: {
    getSuggestions: (operation: string) => Array<string>,
    errors?: ErrorList,
    info?: {
      field: string,
      type: ViolationTypes,
      message: string,
    },
  },
  onFormChange: (value: any, name: string) => void,
  values: {},
};

function DeveloperFormFields(props: Props) {
  const {
    fields,
    meta: { errors, getSuggestions, info },
    onFormChange,
    values,
  } = props;

  return (
    <div>
      {fields.map(field => {
        const value = values[field.key];

        const inputTitleId = `input-${field.key}-title-${Date.now()}`;

        return (
          <SuggestionsProvider getSuggestions={getSuggestions} key={field.key}>
            <NonFormaticFieldWrapper
              helpText={field.helpText}
              inputTitleId={inputTitleId}
              required={field.required}
              title={field.label}
            >
              {field.type === 'code-editor' && (
                <CodeEditor
                  aria-labelledby={inputTitleId}
                  errors={errors?.[field.key]}
                  maxLines={20}
                  minLines={3}
                  name={field.key}
                  onChange={valueNext =>
                    onFormChange({ source: valueNext }, field.key)
                  }
                  readOnly={field.readOnly}
                  value={value && value.source}
                />
              )}
              {field.type === 'request-editor' && (
                <RequestEditor
                  aria-labelledby={inputTitleId}
                  disabledTabs={field.key === 'authorizeUrl' ? ['options'] : []}
                  errors={errors}
                  info={info && field.key === info.field && info}
                  labelText={field.label}
                  name={field.key}
                  onChange={valueNext =>
                    onFormChange(cleanRequest(valueNext), field.key)
                  }
                  value={value}
                  {...field.extraProps}
                />
              )}
              {field.type === 'text-input' && (
                <TextInput
                  aria-labelledby={inputTitleId}
                  isDisabled={field.readOnly}
                  name={field.key}
                  onChange={e => onFormChange(e.target.value, field.key)}
                  value={value}
                />
              )}
              {field.type === 'boolean-input' && (
                <label css={Styles.checkbox}>
                  <Checkbox
                    aria-label={field.key}
                    checked={Boolean(value)}
                    disabled={field.readOnly}
                    hasExternalLabel={true}
                    isDisabled={field.readOnly}
                    name={field.key}
                    onChange={e => onFormChange(e.target.checked, field.key)}
                    type="checkbox"
                    value={field.key}
                  />
                  {field.boolHelpText && (
                    <span css={Styles.checkboxLabel}>{field.boolHelpText}</span>
                  )}
                </label>
              )}
            </NonFormaticFieldWrapper>
          </SuggestionsProvider>
        );
      })}
    </div>
  );
}

export default DeveloperFormFields;
